import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Inbox, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/components/feedback/ConfirmDialog";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { listSources } from "@/services/api/imports";
import {
  createMailbox,
  deleteMailbox,
  listMailboxes,
  updateMailbox,
} from "@/services/api/mailboxes";
import type { MailboxInput } from "@/services/api/mailboxes";
import type { MailboxAccount } from "@/services/api/types";

const POST_ACTIONS_IMAP: Array<{ value: MailboxInput["post_action"]; label: string }> = [
  { value: "leave", label: "Leave in inbox" },
  { value: "move", label: "Move to Processed folder" },
  { value: "flag", label: "Flag as processed" },
];

const POST_ACTIONS_POP3: Array<{ value: MailboxInput["post_action"]; label: string }> = [
  { value: "leave", label: "Leave on server" },
  { value: "move", label: "Delete after import" },
];

function MailboxRow({
  mailbox,
  usedBy,
}: {
  mailbox: MailboxAccount;
  usedBy: string[];
}) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [draft, setDraft] = useState<MailboxInput>({
    protocol: mailbox.protocol,
    host: mailbox.host,
    port: mailbox.port,
    use_tls: mailbox.use_tls,
    username: mailbox.username,
    secret_ref: mailbox.secret_ref,
    folder: mailbox.folder,
    post_action: mailbox.post_action,
    is_active: mailbox.is_active,
  });

  const dirty =
    draft.protocol !== mailbox.protocol ||
    draft.host !== mailbox.host ||
    draft.port !== mailbox.port ||
    draft.use_tls !== mailbox.use_tls ||
    draft.username !== mailbox.username ||
    draft.secret_ref !== mailbox.secret_ref ||
    draft.folder !== mailbox.folder ||
    draft.post_action !== mailbox.post_action ||
    draft.is_active !== mailbox.is_active;

  const save = useMutation({
    mutationFn: () => updateMailbox(mailbox.id, draft),
    onSuccess: () => {
      toast.success("Mailbox saved");
      void queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: () => deleteMailbox(mailbox.id),
    onSuccess: () => {
      toast.success("Mailbox deleted");
      void queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const confirmDelete = async () => {
    const ok = await confirm({
      title: t("admin.deleteRecord", { name: mailbox.username }),
      description: "Remove this mailbox account? Import sources using it will stop polling.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (ok) remove.mutate();
  };

  const set = <K extends keyof MailboxInput>(key: K, value: MailboxInput[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const postActions = draft.protocol === "pop3" ? POST_ACTIONS_POP3 : POST_ACTIONS_IMAP;
  const inUse = usedBy.length > 0;

  return (
    <div className="rounded-md border p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium">
          <Inbox className="h-4 w-4 text-muted-foreground" />
          <span className="font-mono text-sm">{mailbox.username}</span>
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {draft.protocol}
          </span>
          {inUse ? (
            <span className="text-xs text-muted-foreground">
              used by: <span className="font-mono">{usedBy.join(", ")}</span>
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Active
            <Switch checked={draft.is_active} onCheckedChange={(v) => set("is_active", v)} />
          </label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Delete mailbox"
            disabled={remove.isPending || inUse}
            title={inUse ? "Repoint or delete the import sources using this mailbox first" : undefined}
            onClick={() => void confirmDelete()}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <Label className="text-xs">Protocol</Label>
          <Select value={draft.protocol} onValueChange={(v) => set("protocol", v as MailboxInput["protocol"])}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="imap">IMAP</SelectItem>
              <SelectItem value="pop3">POP3</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Host</Label>
          <Input value={draft.host} onChange={(e) => set("host", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Port</Label>
          <Input
            type="number"
            value={draft.port}
            onChange={(e) => set("port", Number(e.target.value) || 0)}
          />
        </div>
        <div>
          <Label className="text-xs">Use TLS</Label>
          <div className="flex h-9 items-center">
            <Switch checked={draft.use_tls} onCheckedChange={(v) => set("use_tls", v)} />
          </div>
        </div>
        <div>
          <Label className="text-xs">Username</Label>
          <Input value={draft.username} onChange={(e) => set("username", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Secret env var</Label>
          <Input
            className="font-mono"
            value={draft.secret_ref}
            onChange={(e) => set("secret_ref", e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs">Folder (IMAP)</Label>
          <Input
            value={draft.folder}
            disabled={draft.protocol === "pop3"}
            onChange={(e) => set("folder", e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs">After import</Label>
          <Select
            value={draft.post_action}
            onValueChange={(v) => set("post_action", v as MailboxInput["post_action"])}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {postActions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

/** Mailbox accounts (IMAP/POP3) used by import sources, edited in place. */
export function MailboxesSection() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["mailboxes"], queryFn: () => listMailboxes() });
  const sources = useQuery({ queryKey: ["import-sources"], queryFn: () => listSources() });

  const add = useMutation({
    mutationFn: () =>
      createMailbox({
        protocol: "imap",
        host: "mail.example.com",
        port: 993,
        use_tls: true,
        username: "imports@example.com",
        secret_ref: "MAILBOX_SECRET",
        folder: "INBOX",
        post_action: "leave",
        is_active: true,
      }),
    onSuccess: () => {
      toast.success("Mailbox created");
      void queryClient.invalidateQueries({ queryKey: ["mailboxes"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const mailboxes = query.data ?? [];
  const usedBy = useMemo(() => {
    const map = new Map<number, string[]>();
    for (const source of sources.data?.items ?? []) {
      // Type-anchor sources (manual master-data imports) have no mailbox.
      if (source.mailbox_account_id === null) continue;
      const list = map.get(source.mailbox_account_id) ?? [];
      list.push(source.code);
      map.set(source.mailbox_account_id, list);
    }
    return map;
  }, [sources.data]);

  return (
    <div className="rounded-md border">
      <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2">
        <div>
          <Label className="text-sm font-semibold">Mailboxes</Label>
          <p className="text-xs text-muted-foreground">
            IMAP/POP3 inboxes polled by import sources. The password lives in the env var named by
            &ldquo;Secret env var&rdquo;. A mailbox used by an import source cannot be deleted until
            the source is repointed or removed.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => add.mutate()} disabled={add.isPending}>
          <Plus className="h-4 w-4" />
          Add mailbox
        </Button>
      </div>
      <div className="space-y-3 p-4">
        {query.isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : mailboxes.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            No mailboxes configured. Add one to start polling an inbox.
          </p>
        ) : (
          mailboxes.map((mailbox) => (
            <MailboxRow
              key={mailbox.id}
              mailbox={mailbox}
              usedBy={usedBy.get(mailbox.id) ?? []}
            />
          ))
        )}
      </div>
    </div>
  );
}
